import React, { useState, useRef } from 'react';
import {
	PlusIcon,
	PhotoIcon,
	DocumentIcon,
	XMarkIcon,
	FolderOpenIcon,
	ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

import useModal from '../../hooks/useModal';

// File type icons
const getFileIcon = (fileName) => {
	if (!fileName) return DocumentIcon;
	const ext = fileName.split('.').pop()?.toLowerCase();
	const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
	if (imageExts.includes(ext)) return PhotoIcon;
	return DocumentIcon;
};

// Check if file is an image
const isImage = (fileName) => {
	if (!fileName) return false;
	const ext = fileName.split('.').pop()?.toLowerCase();
	const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
	return imageExts.includes(ext);
};

// Format file size
const formatFileSize = (bytes) => {
	if (!bytes) return '';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StepAttachments = ({
	attachments = [],
	onAttachmentsChange,
	maxFiles = 5,
	acceptedTypes = 'image/*,.pdf,.doc,.docx,.xls,.xlsx',
	disabled = false,
}) => {
	const [showUploadOptions, setShowUploadOptions] = useState(false);
	const fileInputRef = useRef(null);
	const { setOpen: openDocumentPicker } = useModal('documentPicker');
	const { setOpen: openDocumentPreview } = useModal('documentPreview');

	// Handle file selection
	const handleFileSelect = (e) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		// Check max files limit
		const remainingSlots = maxFiles - attachments.length;
		const filesToAdd = files.slice(0, remainingSlots);

		// Convert files to attachment objects with preview
		const newAttachments = filesToAdd.map((file) => ({
			id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			name: file.name,
			size: file.size,
			type: file.type,
			file: file, // Keep the file object for upload
			isNew: true,
			// Create preview URL for images
			previewUrl: file.type.startsWith('image/')
				? URL.createObjectURL(file)
				: null,
		}));

		onAttachmentsChange([...attachments, ...newAttachments]);
		setShowUploadOptions(false);

		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	// Handle remove attachment
	const handleRemove = (attachmentId) => {
		const attachment = attachments.find((a) => a.id === attachmentId);
		// Revoke object URL if it exists
		if (attachment?.previewUrl) {
			URL.revokeObjectURL(attachment.previewUrl);
		}
		onAttachmentsChange(attachments.filter((a) => a.id !== attachmentId));
	};

	// Handle select from existing documents
	const handleSelectExisting = () => {
		setShowUploadOptions(false);
		const remainingSlots = maxFiles - attachments.length;
		openDocumentPicker({
			multiSelect: true,
			maxSelect: remainingSlots,
			onSelect: (selectedDocs) => {
				// Convert selected documents to attachment format
				const newAttachments = selectedDocs.map((doc) => ({
					id: doc.id,
					documentId: doc.id,
					name: doc.name || doc.originalName,
					size: doc.size,
					type: doc.contentType,
					url: doc.location,
					previewUrl: doc.contentType?.startsWith('image/') ? doc.location : null,
					isExisting: true,
				}));
				onAttachmentsChange([...attachments, ...newAttachments]);
			},
		});
	};

	const canAddMore = attachments.length < maxFiles && !disabled;

	return (
		<div className="mt-3">
			{/* Attachments Grid */}
			{attachments.length > 0 && (
				<div className="flex flex-wrap gap-2 mb-2">
					{attachments.map((attachment) => {
						const FileIcon = getFileIcon(attachment.name);
						const showPreview =
							isImage(attachment.name) && attachment.previewUrl;

						return (
							<div
								key={attachment.id}
								className="relative group flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
							>
								{/* Thumbnail or Icon - clickable for preview */}
								{showPreview ? (
									<img
										src={attachment.previewUrl}
										alt={attachment.name}
										className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
										onClick={() => openDocumentPreview(attachment)}
										title="Click to preview"
									/>
								) : (
									<div
										className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition-colors"
										onClick={() => openDocumentPreview(attachment)}
										title="Click to preview"
									>
										<FileIcon className="w-5 h-5 text-gray-500" />
									</div>
								)}

								{/* File Info */}
								<div className="flex-1 min-w-0 max-w-[120px]">
									<p className="text-xs font-medium text-gray-700 truncate">
										{attachment.name}
									</p>
									{attachment.size && (
										<p className="text-xs text-gray-400">
											{formatFileSize(attachment.size)}
										</p>
									)}
								</div>

								{/* Remove button */}
								{!disabled && (
									<button
										type="button"
										onClick={() => handleRemove(attachment.id)}
										className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-100 rounded-full text-red-600 hover:bg-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
									>
										<XMarkIcon className="w-3.5 h-3.5" />
									</button>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Add Attachment Button */}
			{canAddMore && (
				<div className="relative">
					<button
						type="button"
						onClick={() => setShowUploadOptions(!showUploadOptions)}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
					>
						<PlusIcon className="w-3.5 h-3.5" />
						Add Attachment
					</button>

					{/* Upload Options Dropdown */}
					{showUploadOptions && (
						<div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
							<button
								type="button"
								onClick={() => fileInputRef.current?.click()}
								className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
							>
								<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
								Upload File
							</button>
							<button
								type="button"
								onClick={handleSelectExisting}
								className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
							>
								<FolderOpenIcon className="w-4 h-4 text-gray-500" />
								Choose from Library
							</button>
						</div>
					)}

					{/* Hidden file input */}
					<input
						ref={fileInputRef}
						type="file"
						accept={acceptedTypes}
						multiple
						onChange={handleFileSelect}
						className="hidden"
					/>
				</div>
			)}

			{/* Click outside to close dropdown */}
			{showUploadOptions && (
				<div
					className="fixed inset-0 z-40"
					onClick={() => setShowUploadOptions(false)}
				/>
			)}

			</div>
	);
};

export default StepAttachments;
